import { useEffect, useRef } from 'react';

export function useHorizontalEdgeScroll() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const animationFrame = useRef<number>();
  const mouseX = useRef<number>(0);
  const isInside = useRef(false);

  useEffect(() => {
    const container = scrollContainerRef.current;

    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.current = e.clientX;
    };

    const handleMouseEnter = () => {
      isInside.current = true;
    };

    const handleMouseLeave = () => {
      isInside.current = false;
    };

    const scrollLoop = () => {
      if (isInside.current) {
        const rect = container.getBoundingClientRect();
        const offsetX = mouseX.current - rect.left;

        const scrollThreshold = 100;
        const maxSpeed = 10;

        let delta = 0;

        if (offsetX < scrollThreshold) {
          delta = -Math.max(maxSpeed * (1 - offsetX / scrollThreshold), 1);
        } else if (rect.width - offsetX < scrollThreshold) {
          const distRight = rect.width - offsetX;
          delta = Math.max(maxSpeed * (1 - distRight / scrollThreshold), 1);
        }

        if (delta !== 0) {
          container.scrollLeft += delta;
        }
      }

      animationFrame.current = requestAnimationFrame(scrollLoop);
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    container.addEventListener('dragover', handleMouseMove);
    container.addEventListener('dragenter', handleMouseEnter);
    container.addEventListener('dragend', handleMouseLeave);

    animationFrame.current = requestAnimationFrame(scrollLoop);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);

      container.removeEventListener('dragover', handleMouseMove);
      container.removeEventListener('dragenter', handleMouseEnter);
      container.removeEventListener('dragend', handleMouseLeave);

      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return { innerRef: scrollContainerRef };
}

export function useWindowVerticalEdgeScroll() {
  const isDragging = useRef(false);

  function handleDragStart() {
    isDragging.current = true;
  }

  function handleDragEnd() {
    isDragging.current = false;
  }

  function handleDragOver(e: React.DragEvent) {
    if (!isDragging.current) return;

    const scrollThreshold = 200;
    const scrollSpeed = 40;

    const { clientY } = e;
    const viewportHeight = window.innerHeight;

    if (clientY < scrollThreshold) {
      const speed = Math.max(scrollSpeed * (1 - clientY / scrollThreshold), 2);
      window.scrollBy({ top: -speed, behavior: 'auto' });
    } else if (viewportHeight - clientY < scrollThreshold) {
      const distanceFromBottom = viewportHeight - clientY;
      const speed = Math.max(
        scrollSpeed * (1 - distanceFromBottom / scrollThreshold),
        2,
      );
      window.scrollBy({ top: speed, behavior: 'auto' });
    }
  }

  return { handleDragStart, handleDragOver, handleDragEnd };
}
